import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { IoMdDownload } from "react-icons/io";
import { useParams } from 'react-router-dom';
import { ActionIcon, Button, Tooltip } from 'rizzui';
import DeletePopover from '../../../../../components/buttons/DeletePopover';
import { Loader } from '../../../../../components/loader/SpinnerLogo';
import { FilterDrawerView } from '../../../../../components/table/components/TableFilter';
import CustomTable from '../../../../../components/table/CustomTable';
import useHandleDownloadDocument from '../../../../../hooks/useDownloadDocument';
import useFetch from '../../../../../hooks/useFetch';
import useFilters from '../../../../../hooks/useFilters';
import { DocumentsService } from '../../../../../services/document/documentsService';
import { UserService } from '../../../../../services/user/userService';
import DocumentsFilters from './DocumentsFilters';
import cn from '../../../../../utils/classNames';
import UserDocumentsFormModal from '../../../components/UserDocumentsFormModal';
import StatusDropdown from '../../../../../components/forms/StatusDropdown';
import useDocuments from '../../../../../hooks/api-calls/useDocuments';
import { toast } from 'sonner';
import useHandleErrors from '../../../../../hooks/useHandleErrors';

interface UserDocumentsProps { }

const columnOrder = [
    'id',
    'name',
    'category',
    'description',
    'extension',
    'createdAt',
];

const UserDocuments: React.FC<UserDocumentsProps> = ({ }) => {

    // STATES

    const [updateList, setUpdateList] = useState<boolean>(false);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [ selectedDocument, setSelectedDocument ] = useState<any>(null);
    const { getDocumentStatusList } = useDocuments();

    // HOOKS

    const { id = '' } = useParams<{ id: string }>();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFilters({isUserProfile: true, user: id});
    const { handleDownloadDocumentNewTab } = useHandleDownloadDocument();
    const docService = new DocumentsService();
    const service = new UserService();
    const { handleErrors } = useHandleErrors();

    // DATA FETCHING

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!filters.filter_filters?.user) return null;
        const response = await service.listUserDocuments(filters);
        return response.getResponseData();
    }, [filters]));

    // FUNCTIONS

    // --------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA EL BORRADO MULTIPLE DE DOCUMENTOS
     * @EN HANDLES THE MULTIPLE DELETION OF DOCUMENTS
     * 
     * @param ids 
     */
    // --------------------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        await docService.multiDeleteDocuments(ids);
        setUpdateList(true);
        refetch();
    };
    // --------------------------------------------------------------------------------------------------------------------------

    // --------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA EL ORDEN DE LAS COLUMNAS
     * @EN HANDLES THE ORDER OF THE COLUMNS
     * 
     * @param data
     * @param columnOrder
     */
    // --------------------------------------------------------------------------------------------------------------------------
    const orderedData = data?.data.map((row: any) => {
        const formattedRow: any = {
            id: row?.id,
            name: row?.name,
            category: row?.category,
            description: row?.description,
            status: row?.status,
            extension: row?.document?.extension,

            createdAt: moment(row?.createdAt?.date).format('DD/MM/YYYY'),
        };

        const orderedRow: any = {};
        columnOrder.forEach((column) => {
            orderedRow[column] = formattedRow[column];
        });

        return orderedRow;
    });
    
    // --------------------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIA EL ESTADO DE UN DOCUMENTO
     * @EN CHANGES THE STATUS OF A DOCUMENT
     * 
     * @param id
     * @param status
     */
    // -------------------------------------------------------------------------------------------------------------
    const changeStatus = async (id: string, status: string) => {
        try{
            const response = await(await docService.changeStatusDocument(id, status)).getResponseData();
            if(response.success) {
                toast.success('Estado del documento cambiado correctamente');
                refetch();
            }else{
                handleErrors(response);
            }
        }catch(error) {
            handleErrors(error);
        }
    }
    // -------------------------------------------------------------------------------------------------------------
    
    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CERRAR MODAL
     * @EN CLOSE MODAL
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const onClose =  () => {
        setIsOpen(false);
        setTimeout(() => {  
            setSelectedDocument(null);
        }
        , 150);
    };
    //-----------------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        updateFilters({ user: id });
    }, [id]);

    useEffect(() => {
        if (updateList) {
            refetch();
            setUpdateList(false);
        }
    }, [updateList]);

    // RENDER

    return (
        <>
            <div className="container mx-auto p-4">
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                    <div className="md:col-span-1 col-span-1">
                        <div className="pt-7 mb-10 grid gap-7 divide-y-2 divide-dashed divide-gray-200">
                            {loading
                                ? <Loader />
                                : data?.data && (
                                    <CustomTable
                                        id={'users-documents-table'}
                                        columnOrder={columnOrder}
                                        data={orderedData}
                                        columnsNotShown={['extension', 'fileName']}
                                       addicionalButtons={
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    color="primary"
                                                    onClick={() => {  setIsOpen(true); }}
                                                    className={cn(['mt-4 md:mt-0'])}
                                                >
                                                    Subir Documento
                                                </Button>
                                            </div>  
                                        }
                                        actions={[
                                            {
                                                label: 'Descargar',
                                                render: (row: any) => {
                                                    return (
                                                        <Tooltip
                                                            size="sm"
                                                            content={'Descargar documento'}
                                                            placement="top"
                                                            color="invert"
                                                        >
                                                            <ActionIcon
                                                                as="span"
                                                                size="sm"
                                                                variant="outline"
                                                                className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer bg-white"
                                                                onClick={() => handleDownloadDocumentNewTab(
                                                                    docService.downloadDocument,
                                                                    row.id,
                                                                    row.name,
                                                                    row?.extension
                                                                )}
                                                            >
                                                                <IoMdDownload className="h-4 w-4" size={20} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    );
                                                },
                                            },
                                            {
                                                label: 'Delete',
                                                render: (row: any) => {
                                                    return (
                                                        <DeletePopover
                                                            title={`Eliminar documento`}
                                                            description={`¿Estás seguro de que deseas eliminar ${row.name}?`}
                                                            onDelete={() => handleMultiDelete([row.id])}
                                                        />
                                                    );
                                                },
                                            },
                                        ]}
                                        handleMultipleDelete={handleMultiDelete}
                                        filters={filters}
                                        updateFilters={updateFilters}
                                        updateFilterOrder={updateFilterOrder}
                                        defaultOrder={filters.filter_order || undefined}
                                        paginationData={{
                                            pageSize: filters.limit,
                                            currentPage: filters.page,
                                            pageCount: data ? data.lastPage : 1,
                                            totalCount: data?.totalRegisters,
                                            handlePagination: updatePage,
                                            handlePerPage: updatePageSize,
                                        }}
                                        toggleFilters={() => setOpenFilters(!openFilters)}
                                        search={false}
                                    />
                                )
                            }
                        </div>
                    </div>
                </div>
            </div>

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Documentos'}>
                <div className="grid grid-cols-1 gap-6">
                    <DocumentsFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
                </div>
            </FilterDrawerView>

            <UserDocumentsFormModal
                isOpen={isOpen}
                onClose={onClose}
                refetch={refetch}
                document={selectedDocument}
                isAdmin={false}
                userId={id}
            />
        </>
    );
};

export default UserDocuments;